import {
  AutoStoriesOutlined,
  HelpOutline,
  PlayArrowOutlined,
  ReportGmailerrorredOutlined,
  TaskAltOutlined,
  TonalityOutlined,
} from '@mui/icons-material';
import { Box, Stack, styled, Typography } from '@mui/material';
import startCase from 'lodash/startCase';
import React, { useMemo } from 'react';

import { DataRequestItem, Event } from '../../../Types/dealGPT';
import {
  DataRequestItemStatus,
  dataRequestItemStatusColors,
  EventType,
  HistoryViewMode,
} from '../../../Types/enums';
import { formatDate } from '../../../util/dateUtils';

const TimelineLine = styled(Box)(({ theme }) => ({
  width: 2,
  flexGrow: 1,
  backgroundColor: theme.palette.divider,
  marginLeft: 'auto',
  marginRight: 'auto',
  height: '75%',
  marginTop: '12.5%',
  marginBottom: '12.5%',
}));

const DEFAULT_COLOR = '#50658E';
const PRIORITY_COLOR = '#070154';

export interface HistoryEntryProps {
  event: Event;
  isLast: boolean;
  viewMode: HistoryViewMode;
}

const HistoryEntry: React.FC<HistoryEntryProps> = ({ event, isLast, viewMode }) => {
  const getValueColor = (field: string, value: string): string => {
    if (field === 'status') {
      return dataRequestItemStatusColors[value as DataRequestItemStatus] || DEFAULT_COLOR;
    } else if (field === 'priority') {
      return PRIORITY_COLOR;
    } else {
      return DEFAULT_COLOR;
    }
  };

  const getStatusIcon = (field: string, value: string) => {
    const color = getValueColor(field, value);

    if (field === 'priority') {
      return <ReportGmailerrorredOutlined sx={{ fontSize: 24, color }} />;
    }

    if (field === 'status') {
      switch (value) {
        case DataRequestItemStatus.COMPLETE:
          return <TaskAltOutlined sx={{ fontSize: 24, color }} />;
        case DataRequestItemStatus.OPEN:
          return <AutoStoriesOutlined sx={{ fontSize: 24, color }} />;
        case DataRequestItemStatus.PARTIAL:
          return <TonalityOutlined sx={{ fontSize: 24, color }} />;
        default:
          return <HelpOutline sx={{ fontSize: 24, color }} />;
      }
    }

    return <HelpOutline sx={{ fontSize: 24, color: DEFAULT_COLOR }} />;
  };

  const eventChanges = useMemo(() => {
    if (!event.payload?.updates) {
      return [];
    }

    const updates = event.payload.updates;
    const original = event.payload.original;

    return Object.keys(updates)
      .filter((key) => key !== 'id')
      .map((key) => ({
        field: key,
        from: original ? String(original[key as keyof DataRequestItem] || '') : '',
        to: String(updates[key as keyof DataRequestItem] || ''),
      }));
  }, [event]);

  return event.eventType === EventType.DRL_ROW_UPDATE ? (
    <Stack spacing={1.5}>
      {eventChanges.map((change, idx) => (
        <Box key={idx} display="flex" flexDirection="row" gap={1.5}>
          <Box display="flex" flexDirection="column" alignItems="center" width={24}>
            {getStatusIcon(change.field, change.to)}
            {!(isLast && idx === eventChanges.length - 1) && <TimelineLine />}
          </Box>
          <Box display="flex" flexDirection="column" pb={2} flex={1}>
            {viewMode === HistoryViewMode.DETAILED ? (
              <>
                <Typography variant="body2">
                  <Typography component="span" variant="body2" fontWeight={600}>
                    {event.payload?.key || ''}
                  </Typography>{' '}
                  {startCase(change.field).toLowerCase()} changed from{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: getValueColor(change.field, change.from) }}
                  >
                    {startCase(change.from)}
                  </Typography>{' '}
                  to{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: getValueColor(change.field, change.to) }}
                  >
                    {change.to.toUpperCase()}
                  </Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: '#475467' }}>
                  by {event.userPreferredName || 'System'} on {formatDate(event.createdAt)}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2">
                  <Typography component="span" variant="body2" fontWeight={600}>
                    {event.payload?.key || ''}
                  </Typography>{' '}
                  {startCase(change.field).toLowerCase()} set to:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: getValueColor(change.field, change.to) }}
                >
                  {change.to.toUpperCase()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#475467' }}>
                  {formatDate(event.createdAt)}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  ) : (
    <Box display="flex" flexDirection="row" gap={1.5}>
      <Box display="flex" flexDirection="column" alignItems="center" width={24}>
        <PlayArrowOutlined sx={{ fontSize: 24, color: '#B641FF' }} />
        {!isLast && <TimelineLine />}
      </Box>
      <Box display="flex" flexDirection="column" pb={2} flex={1}>
        <Typography variant="body2" fontWeight={600}>
          PROJECT:
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ color: '#B641FF' }}>
          {event.eventType.toUpperCase()}
        </Typography>
        <Typography variant="caption" sx={{ color: '#475467' }}>
          {event.userPreferredName || 'Run by System'}
        </Typography>
        <Typography variant="caption" sx={{ color: '#475467' }}>
          {formatDate(event.createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};

export default HistoryEntry;
